// src/pages/ManagerDashboard.jsx
import React, { useEffect, useState } from "react";
import { collection, query, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";
import { createCoachNotification, createRefereeNotification } from "../utils/notifications";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [referees, setReferees] = useState([]);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("");
  const [declinedGame, setDeclinedGame] = useState(null);
  const [defaultRates, setDefaultRates] = useState({});
  const [editingBilling, setEditingBilling] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({
    pending: false,
    assigned: false,
    scheduled: false,
    completed: false
  });

  // ---------------- LOAD DEFAULT BILLING RATES ----------------
  useEffect(() => {
    async function loadRates() {
      if (!user) return;
      
      try {
        const ratesDoc = await getDoc(doc(db, "billingRates", user.uid));
        if (ratesDoc.exists()) {
          setDefaultRates(ratesDoc.data());
        }
      } catch (err) {
        console.error("Error loading default rates:", err);
      }
    }
    
    loadRates();
  }, [user]);

  // Helper function to get the appropriate default rate for a role based on competition
  function getDefaultRate(game, role) {
    if (!defaultRates) return "";
    
    // Check if there's a competition-specific rate (case-insensitive)
    if (defaultRates.competitionRates && game.competition) {
      // Find matching competition (case-insensitive)
      const matchingComp = Object.keys(defaultRates.competitionRates).find(
        comp => comp.toLowerCase() === game.competition.toLowerCase()
      );
      
      if (matchingComp) {
        const compRate = defaultRates.competitionRates[matchingComp]?.[role];
        if (compRate) return compRate;
      }
    }
    
    // Fall back to default rate
    return defaultRates[role] || "";
  }

  // Helper to check if using competition-specific rate
  function isCompetitionRate(game, role) {
    if (!defaultRates || !defaultRates.competitionRates || !game.competition) return false;
    
    // Find matching competition (case-insensitive)
    const matchingComp = Object.keys(defaultRates.competitionRates).find(
      comp => comp.toLowerCase() === game.competition.toLowerCase()
    );
    
    return matchingComp ? !!defaultRates.competitionRates[matchingComp]?.[role] : false;
  }

  // Toggle edit mode for billing
  function toggleEditBilling(gameId, role) {
    const key = `${gameId}_${role}`;
    setEditingBilling({ ...editingBilling, [key]: !editingBilling[key] });
  }

  // Apply default rate if not already set
  async function applyDefaultRate(gameId, role, rate) {
    if (!rate) return;
    try {
      await updateDoc(doc(db, "games", gameId), {
        [`billing_${role}`]: rate,
        billingUpdatedAt: new Date(),
      });
    } catch (err) {
      alert("Error applying rate: " + err.message);
    }
  }

  // Apply ALL default rates for a game at once
  async function applyAllDefaultRates(game) {
    try {
      const updates = {};
      
      // Check each role that has an official assigned
      if (game.assignedRefereeId && !game.billing_referee) {
        const rate = getDefaultRate(game, "referee");
        if (rate) updates.billing_referee = rate;
      }
      if (game.assistantReferee1Id && !game.billing_assistant1) {
        const rate = getDefaultRate(game, "assistant1");
        if (rate) updates.billing_assistant1 = rate;
      }
      if (game.assistantReferee2Id && !game.billing_assistant2) {
        const rate = getDefaultRate(game, "assistant2");
        if (rate) updates.billing_assistant2 = rate;
      }
      if (game.assistant4Id && !game.billing_assistant4) {
        const rate = getDefaultRate(game, "assistant4");
        if (rate) updates.billing_assistant4 = rate;
      }
      if (game.assistant5Id && !game.billing_assistant5) {
        const rate = getDefaultRate(game, "assistant5");
        if (rate) updates.billing_assistant5 = rate;
      }
      if (game.timeKeeperId && !game.billing_timekeeper) {
        const rate = getDefaultRate(game, "timekeeper");
        if (rate) updates.billing_timekeeper = rate;
      }
      if (game.playerMovementRecorderId && !game.billing_playermovement) {
        const rate = getDefaultRate(game, "playermovement");
        if (rate) updates.billing_playermovement = rate;
      }
      if (game.statisticianId && !game.billing_statistician) {
        const rate = getDefaultRate(game, "statistician");
        if (rate) updates.billing_statistician = rate;
      }
      if (game.technicalZoneManagerId && !game.billing_technicalzone) {
        const rate = getDefaultRate(game, "technicalzone");
        if (rate) updates.billing_technicalzone = rate;
      }
      if (game.pointOfOrigin1Id && !game.billing_pointoforigin1) {
        const rate = getDefaultRate(game, "pointoforigin1");
        if (rate) updates.billing_pointoforigin1 = rate;
      }
      if (game.pointOfOrigin2Id && !game.billing_pointoforigin2) {
        const rate = getDefaultRate(game, "pointoforigin2");
        if (rate) updates.billing_pointoforigin2 = rate;
      }

      if (Object.keys(updates).length > 0) {
        updates.billingUpdatedAt = new Date();
        await updateDoc(doc(db, "games", game.id), updates);
        alert("Default rates applied successfully!");
      } else {
        alert("All rates are already set for this game.");
      }
    } catch (err) {
      alert("Error applying rates: " + err.message);
    }
  }

  // Render billing row component
  function renderBillingRow(game, role, label, name) {
    const billingField = `billing_${role}`;
    const editKey = `${game.id}_${role}`;
    const defaultRate = getDefaultRate(game, role);
    const currentRate = game[billingField];
    const isEditing = editingBilling[editKey];

    return (
      <div>
        <label className="block text-xs font-semibold mb-1">
          {label}: {name}
          {defaultRate && !currentRate && (
            <span className={`ml-2 font-normal text-[10px] ${
              isCompetitionRate(game, role) ? "text-purple-600" : "text-blue-600"
            }`}>
              ({isCompetitionRate(game, role) ? game.competition : "Default"}: R{defaultRate})
            </span>
          )}
        </label>
        {isEditing ? (
          <div className="flex gap-1">
            <div className="relative flex-1">
              <span className="absolute left-2 top-1.5 text-gray-600 text-xs">R</span>
              <input
                type="number"
                className="w-full p-1.5 pl-6 border rounded text-xs"
                placeholder="0"
                defaultValue={currentRate || defaultRate || ""}
                onBlur={(e) => {
                  if (e.target.value) {
                    updateGameBilling(game.id, role, e.target.value);
                  }
                  toggleEditBilling(game.id, role);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                  }
                }}
                autoFocus
              />
            </div>
            <button
              onClick={() => toggleEditBilling(game.id, role)}
              className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {(currentRate || defaultRate) ? (
              <>
                <p className="text-sm font-semibold text-green-600">
                  R{currentRate || defaultRate}
                </p>
                <button
                  onClick={() => {
                    if (!currentRate && defaultRate) {
                      applyDefaultRate(game.id, role, defaultRate);
                    }
                    toggleEditBilling(game.id, role);
                  }}
                  className="ml-auto px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                >
                  Edit
                </button>
              </>
            ) : (
              <p className="text-xs text-gray-500">No rate set</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---------------- LOAD GAMES ----------------
  useEffect(() => {
    const q = query(collection(db, "games"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const newGames = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGames(newGames);
      },
      (err) => {
        console.error("Games read error:", err);
        setError(err.message);
      }
    );

    return unsub;
  }, []); // Remove games dependency to prevent infinite loop

  // ---------------- LOAD REFEREES ----------------
  useEffect(() => {
    const q = query(collection(db, "users"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const allUsers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReferees(allUsers.filter(u => u.role === "referee" && u.approved === true));
      },
      (err) => {
        console.error("Referee read error:", err);
        setError(err.message);
      }
    );

    return unsub;
  }, []);

  async function assignReferee(gameId, refereeId, role = "referee") {
    try {
      const referee = referees.find(r => r.id === refereeId);
      const game = games.find(g => g.id === gameId);
      if (!referee) {
        alert("Referee not found");
        return;
      }

      // Check if referee is already assigned to another position in this game
      const existingPositions = [];
      if (game.assignedRefereeId === refereeId && role !== "referee") existingPositions.push("Referee");
      if (game.assistantReferee1Id === refereeId && role !== "assistantReferee1") existingPositions.push("Assistant Referee 1");
      if (game.assistantReferee2Id === refereeId && role !== "assistantReferee2") existingPositions.push("Assistant Referee 2");
      if (game.assistant4Id === refereeId && role !== "assistant4") existingPositions.push("Assistant 4");
      if (game.assistant5Id === refereeId && role !== "assistant5") existingPositions.push("Assistant 5");
      if (game.timeKeeperId === refereeId && role !== "timeKeeper") existingPositions.push("Time Keeper");
      if (game.playerMovementRecorderId === refereeId && role !== "playerMovementRecorder") existingPositions.push("Player Movement Recorder");
      if (game.statisticianId === refereeId && role !== "statistician") existingPositions.push("Statistician");
      if (game.technicalZoneManagerId === refereeId && role !== "technicalZoneManager") existingPositions.push("Technical Zone Manager");

      if (existingPositions.length > 0) {
        alert(
          `Cannot assign ${referee.fullName}!\n\n` +
          `They are already assigned to this game as: ${existingPositions.join(", ")}\n\n` +
          `A referee cannot hold multiple positions in the same game.`
        );
        return;
      }

      // Check for time conflicts with referee's existing assignments
      const refereeGames = games.filter(g => 
        (g.assignedRefereeId === refereeId || 
         g.assistantReferee1Id === refereeId || 
         g.assistantReferee2Id === refereeId ||
         g.assistant4Id === refereeId ||
         g.assistant5Id === refereeId ||
         g.timeKeeperId === refereeId ||
         g.playerMovementRecorderId === refereeId ||
         g.statisticianId === refereeId ||
         g.technicalZoneManagerId === refereeId) &&
        g.id !== gameId &&
        g.refereeStatus !== "declined" &&
        g.status !== "cancelled"
      );

      const conflictingGame = refereeGames.find(existingGame => {
        // Only check games on the same date
        if (existingGame.date !== game.date) return false;
        
        // Exception: Skip conflict check if either game is 7s
        if (existingGame.competition === "7s" || game.competition === "7s") {
          return false;
        }
        
        // Parse times (assuming format like "14:00" or "2:00 PM")
        const parseTime = (timeStr) => {
          if (!timeStr) return null;
          const [hours, minutes] = timeStr.split(':').map(s => parseInt(s));
          return hours * 60 + minutes;
        };

        const existingTime = parseTime(existingGame.time);
        const newTime = parseTime(game.time);
        
        if (!existingTime || !newTime) return false;
        
        // Calculate time difference in minutes
        const timeDiff = Math.abs(existingTime - newTime);
        
        // Conflict if within 60 minutes
        return timeDiff < 60;
      });

      if (conflictingGame) {
        alert(
          `Cannot assign ${referee.fullName}!\n\n` +
          `They are already assigned to:\n` +
          `${conflictingGame.teamA} vs ${conflictingGame.teamB}\n` +
          `${conflictingGame.date} @ ${conflictingGame.time}\n\n` +
          `Games must have at least 60 minutes gap between them.`
        );
        return;
      }

      // Update based on role
      const updates = {};
      
      if (role === "referee") {
        updates.assignedRefereeId = refereeId;
        updates.assignedRefereeName = referee.fullName;
        updates.assignedRefereeLeague = referee.currentLeague || "";
        updates.status = "assigned";
        updates.refereeStatus = "pending";
        updates.assignedAt = new Date();
      } else if (role === "assistant1") {
        updates.assistantReferee1Id = refereeId;
        updates.assistantReferee1Name = referee.fullName;
        updates.assistantReferee1League = referee.currentLeague || "";
      } else if (role === "assistant2") {
        updates.assistantReferee2Id = refereeId;
        updates.assistantReferee2Name = referee.fullName;
        updates.assistantReferee2League = referee.currentLeague || "";
      } else if (role === "assistant4") {
        updates.assistant4Id = refereeId;
        updates.assistant4Name = referee.fullName;
        updates.assistant4League = referee.currentLeague || "";
      } else if (role === "assistant5") {
        updates.assistant5Id = refereeId;
        updates.assistant5Name = referee.fullName;
        updates.assistant5League = referee.currentLeague || "";
      } else if (role === "timekeeper") {
        updates.timeKeeperId = refereeId;
        updates.timeKeeperName = referee.fullName;
        updates.timeKeeperLeague = referee.currentLeague || "";
      } else if (role === "playermovement") {
        updates.playerMovementRecorderId = refereeId;
        updates.playerMovementRecorderName = referee.fullName;
        updates.playerMovementRecorderLeague = referee.currentLeague || "";
      } else if (role === "statistician") {
        updates.statisticianId = refereeId;
        updates.statisticianName = referee.fullName;
        updates.statisticianLeague = referee.currentLeague || "";
      } else if (role === "technicalzone") {
        updates.technicalZoneManagerId = refereeId;
        updates.technicalZoneManagerName = referee.fullName;
        updates.technicalZoneManagerLeague = referee.currentLeague || "";
      }

      await updateDoc(doc(db, "games", gameId), updates);

      // Create notification for referee - new game allocation
      const roleNames = {
        referee: "Main Referee",
        assistant1: "Assistant Referee 1",
        assistant2: "Assistant Referee 2",
        assistant4: "Assistant 4",
        assistant5: "Assistant 5",
        timekeeper: "Time Keeper",
        playermovement: "Player Movement Recorder",
        statistician: "Statistician",
        technicalzone: "Technical Zone Manager"
      };

      await createRefereeNotification(
        refereeId,
        "New Game Assignment",
        `You have been assigned as ${roleNames[role]} for ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time}. Venue: ${game.venue} (Field ${game.field})`
      );

      alert(`${role === "referee" ? "Referee" : "Assistant referee"} assigned successfully!`);
    } catch (err) {
      console.error("Assignment error:", err);
      alert("Failed to assign referee: " + err.message);
    }
  }

  async function unassignReferee(gameId, role = "all") {
    if (!window.confirm(`Remove ${role === "all" ? "all referee assignments" : "this referee"}?`)) return;

    try {
      const game = games.find(g => g.id === gameId);
      const updates = {};
      
      // Store referee IDs for notifications before clearing them
      const removedReferees = [];
      
      if (role === "all" || role === "referee") {
        if (game.assignedRefereeId) removedReferees.push({ id: game.assignedRefereeId, name: game.assignedRefereeName, role: "Main Referee" });
        updates.assignedRefereeId = null;
        updates.assignedRefereeName = null;
        updates.assignedRefereeLeague = null;
        updates.status = "pending";
        updates.refereeStatus = null;
        updates.assignedAt = null;
      }
      
      if (role === "all" || role === "assistant1") {
        if (game.assistantReferee1Id) removedReferees.push({ id: game.assistantReferee1Id, name: game.assistantReferee1Name, role: "Assistant Referee 1" });
        updates.assistantReferee1Id = null;
        updates.assistantReferee1Name = null;
        updates.assistantReferee1League = null;
      }
      
      if (role === "all" || role === "assistant2") {
        if (game.assistantReferee2Id) removedReferees.push({ id: game.assistantReferee2Id, name: game.assistantReferee2Name, role: "Assistant Referee 2" });
        updates.assistantReferee2Id = null;
        updates.assistantReferee2Name = null;
        updates.assistantReferee2League = null;
      }
      
      if (role === "all" || role === "assistant4") {
        if (game.assistant4Id) removedReferees.push({ id: game.assistant4Id, name: game.assistant4Name, role: "Assistant 4" });
        updates.assistant4Id = null;
        updates.assistant4Name = null;
        updates.assistant4League = null;
      }
      
      if (role === "all" || role === "assistant5") {
        if (game.assistant5Id) removedReferees.push({ id: game.assistant5Id, name: game.assistant5Name, role: "Assistant 5" });
        updates.assistant5Id = null;
        updates.assistant5Name = null;
        updates.assistant5League = null;
      }
      
      if (role === "all" || role === "timekeeper") {
        if (game.timeKeeperId) removedReferees.push({ id: game.timeKeeperId, name: game.timeKeeperName, role: "Time Keeper" });
        updates.timeKeeperId = null;
        updates.timeKeeperName = null;
        updates.timeKeeperLeague = null;
      }
      
      if (role === "all" || role === "playermovement") {
        if (game.playerMovementRecorderId) removedReferees.push({ id: game.playerMovementRecorderId, name: game.playerMovementRecorderName, role: "Player Movement Recorder" });
        updates.playerMovementRecorderId = null;
        updates.playerMovementRecorderName = null;
        updates.playerMovementRecorderLeague = null;
      }
      
      if (role === "all" || role === "statistician") {
        if (game.statisticianId) removedReferees.push({ id: game.statisticianId, name: game.statisticianName, role: "Statistician" });
        updates.statisticianId = null;
        updates.statisticianName = null;
        updates.statisticianLeague = null;
      }
      
      if (role === "all" || role === "technicalzone") {
        if (game.technicalZoneManagerId) removedReferees.push({ id: game.technicalZoneManagerId, name: game.technicalZoneManagerName, role: "Technical Zone Manager" });
        updates.technicalZoneManagerId = null;
        updates.technicalZoneManagerName = null;
        updates.technicalZoneManagerLeague = null;
      }

      await updateDoc(doc(db, "games", gameId), updates);

      // Notify removed referees
      for (const ref of removedReferees) {
        await createRefereeNotification(
          ref.id,
          "Removed from Game Assignment",
          `You have been removed from your ${ref.role} assignment for ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time}.`
        );
      }

      alert("Referee unassigned");
    } catch (err) {
      console.error("Unassign error:", err);
      alert("Failed to unassign: " + err.message);
    }
  }

  // Toggle section collapse
  function toggleSection(section) {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }

  async function updateGameBilling(gameId, role, billingRate) {
    try {
      const fieldName = `billing_${role}`;
      await updateDoc(doc(db, "games", gameId), {
        [fieldName]: billingRate,
        billingUpdatedAt: new Date(),
      });
    } catch (err) {
      alert("Error updating billing: " + err.message);
    }
  }

  const filteredGames = games.filter(g => {
    if (dateFilter && g.date !== dateFilter) return false;
    if (ageGroupFilter !== "all" && g.ageGroup !== ageGroupFilter) return false;
    if (venueFilter !== "all" && g.venue !== venueFilter) return false;
    if (timeFilter && g.time !== timeFilter) return false;
    return true;
  });

  // Get unique values for dropdowns
  const uniqueAgeGroups = [...new Set(games.map(g => g.ageGroup).filter(Boolean))];
  const uniqueVenues = [...new Set(games.map(g => g.venue).filter(Boolean))];

  // Split games by status
  const pendingGames = filteredGames.filter(g => g.status === 'pending' || !g.status);
  const assignedGames = filteredGames.filter(g => g.status === 'assigned');
  const scheduledGames = filteredGames.filter(g => g.status === 'scheduled');
  const completedGames = filteredGames.filter(g => g.status === 'completed' || g.status === 'cancelled');

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Referee Manager Dashboard</h2>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* FILTER BAR */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          {/* DATE FILTER */}
          <div className="flex items-center gap-2">
            <label className="font-semibold text-sm">Date:</label>
            <input
              type="date"
              className="p-2 border rounded text-sm"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="px-2 py-1 bg-gray-200 rounded text-xs hover:bg-gray-300"
              >
                Clear
              </button>
            )}
          </div>

          {/* AGE GROUP FILTER */}
          <div className="flex items-center gap-2">
            <label className="font-semibold text-sm">Age Group:</label>
            <select
              className="p-2 border rounded text-sm"
              value={ageGroupFilter}
              onChange={(e) => setAgeGroupFilter(e.target.value)}
            >
              <option value="all">All</option>
              {uniqueAgeGroups.map((age) => (
                <option key={age} value={age}>{age}</option>
              ))}
            </select>
          </div>

          {/* VENUE FILTER */}
          <div className="flex items-center gap-2">
            <label className="font-semibold text-sm">Venue:</label>
            <select
              className="p-2 border rounded text-sm"
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
            >
              <option value="all">All</option>
              {uniqueVenues.map((venue) => (
                <option key={venue} value={venue}>{venue}</option>
              ))}
            </select>
          </div>

          {/* TIME FILTER */}
          <div className="flex items-center gap-2">
            <label className="font-semibold text-sm">Time:</label>
            <input
              type="time"
              className="p-2 border rounded text-sm"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
            />
            {timeFilter && (
              <button
                onClick={() => setTimeFilter("")}
                className="px-2 py-1 bg-gray-200 rounded text-xs hover:bg-gray-300"
              >
                Clear
              </button>
            )}
          </div>

          {/* CLEAR ALL */}
          <button
            onClick={() => {
              setDateFilter("");
              setAgeGroupFilter("all");
              setVenueFilter("all");
              setTimeFilter("");
            }}
            className="ml-auto px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Clear All Filters
          </button>
        </div>
        
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredGames.length} of {games.length} games
          <span className="ml-4 text-xs">
            (Pending: {pendingGames.length} | Assigned: {assignedGames.length} | Scheduled: {scheduledGames.length} | Completed: {completedGames.length})
          </span>
        </div>
      </div>

      {/* HORIZONTAL SECTIONS LAYOUT */}
      <div className="space-y-6">
        {/* SECTION 1: PENDING GAMES */}
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-yellow-600 text-white p-4 rounded-t-lg flex items-center justify-between cursor-pointer hover:bg-yellow-700 transition-colors"
            onClick={() => toggleSection('pending')}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{collapsedSections.pending ? '▶' : '▼'}</span>
              <h3 className="text-xl font-bold">⏳ Pending Games</h3>
            </div>
            <span className="bg-white text-yellow-600 px-3 py-1 rounded-full font-bold">
              {pendingGames.length}
            </span>
          </div>
          {!collapsedSections.pending && (pendingGames.length === 0 ? (
            <p className="text-gray-500 p-6">No pending games.</p>
          ) : (
            <div className="p-4 space-y-4">
              {pendingGames.map((g) => (
            <div
              key={g.id}
              className={`p-4 rounded shadow border-l-4 ${
                // Red if game was declined (has refereeAccepted = false and refereeResponseAt)
                (g.refereeAccepted === false && g.refereeResponseAt)
                  ? "bg-red-100 border-red-600"
                  : "bg-white border-blue-600"
              }`}
            >
              {/* Declined Game Notice */}
              {g.refereeAccepted === false && g.refereeResponseAt && (
                <div className="mb-3 p-2 bg-red-200 border border-red-600 rounded">
                  <p className="text-sm font-semibold text-red-800">
                    ⚠️ This game was declined by a referee and needs reassignment
                  </p>
                </div>
              )}
              
              {/* GAME INFO - HORIZONTAL LAYOUT */}
              <div className="grid grid-cols-12 gap-4 items-start mb-4">
                {/* LEFT SIDE - GAME DETAILS */}
                <div className="col-span-5 space-y-2">
                  {/* Date & Time */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Date & Time:</p>
                    <p className="text-gray-900">{g.date} @ {g.time}</p>
                  </div>
                  
                  {/* Venue & Field */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Venue & Field:</p>
                    <p className="text-gray-900">{g.venue} (Field {g.field})</p>
                  </div>
                  
                  {/* Teams */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Teams:</p>
                    <p className="text-gray-900 font-semibold">{g.teamA} vs {g.teamB}</p>
                  </div>
                  
                  {/* Age & Competition */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Age & Competition:</p>
                    <p className="text-gray-900">{g.ageGroup} • {g.competition}</p>
                  </div>
                  
                  {/* Coach */}
                  <div>
                    <p className="text-xs text-gray-500">Requested by: {g.coachName}</p>
                  </div>
                </div>

                {/* MIDDLE - REFEREE ASSIGNMENTS */}
                <div className="col-span-5 border-l border-r px-4">
                  {/* MAIN REFEREE */}
                  <div className="mb-2">
                    {g.assignedRefereeId ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-green-700 text-sm">
                            Referee: {g.assignedRefereeName}{g.assignedRefereeLeague ? ` - ${g.assignedRefereeLeague}` : ""}
                          </p>
                          <p className="text-xs text-gray-600">
                            {g.refereeStatus?.toUpperCase() || "PENDING"}
                          </p>
                        </div>
                        <button
                          onClick={() => unassignReferee(g.id, "referee")}
                          className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold whitespace-nowrap">Referee:</label>
                        <select
                          className="p-1 border rounded flex-1 text-sm"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              assignReferee(g.id, e.target.value, "referee");
                              e.target.value = "";
                            }
                          }}
                        >
                          <option value="">Select...</option>
                          {referees.map((ref) => (
                            <option key={ref.id} value={ref.id}>
                              {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* ASSISTANT REFEREES - Only show if needed */}
                  {g.needsAssistantReferee && (
                    <div className="space-y-1 bg-gray-50 p-2 rounded mb-2">
                      <h4 className="font-semibold text-xs text-gray-700">Assistant Referees:</h4>
                      
                      {/* ASSISTANT 1 */}
                      <div>
                      {g.assistantReferee1Id ? (
                        <div className="flex items-center justify-between">
                          <p className="text-xs">
                            AR1: {g.assistantReferee1Name}{g.assistantReferee1League ? ` - ${g.assistantReferee1League}` : ""}
                          </p>
                          <button
                            onClick={() => unassignReferee(g.id, "assistant1")}
                            className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label className="text-xs whitespace-nowrap">AR1:</label>
                          <select
                            className="p-1 border rounded flex-1 text-xs"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                assignReferee(g.id, e.target.value, "assistant1");
                                e.target.value = "";
                              }
                            }}
                          >
                            <option value="">Select...</option>
                            {referees.map((ref) => (
                              <option key={ref.id} value={ref.id}>
                                {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {/* ASSISTANT 2 */}
                    <div>
                      {g.assistantReferee2Id ? (
                        <div className="flex items-center justify-between">
                          <p className="text-xs">
                            AR2: {g.assistantReferee2Name}{g.assistantReferee2League ? ` - ${g.assistantReferee2League}` : ""}
                          </p>
                          <button
                            onClick={() => unassignReferee(g.id, "assistant2")}
                            className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label className="text-xs whitespace-nowrap">AR2:</label>
                          <select
                            className="p-1 border rounded flex-1 text-xs"
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                assignReferee(g.id, e.target.value, "assistant2");
                                e.target.value = "";
                              }
                            }}
                          >
                            <option value="">Select...</option>
                            {referees.map((ref) => (
                              <option key={ref.id} value={ref.id}>
                                {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ASSISTANT 4 */}
                {g.needsAssistant4 && (
                  <div className="mb-1 bg-gray-50 p-2 rounded">
                    {g.assistant4Id ? (
                      <div className="flex items-center justify-between">
                        <p className="text-xs">
                          A4: {g.assistant4Name}{g.assistant4League ? ` - ${g.assistant4League}` : ""}
                        </p>
                        <button
                          onClick={() => unassignReferee(g.id, "assistant4")}
                          className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <label className="text-xs whitespace-nowrap">A4:</label>
                        <select
                          className="p-1 border rounded flex-1 text-xs"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              assignReferee(g.id, e.target.value, "assistant4");
                              e.target.value = "";
                            }
                          }}
                        >
                          <option value="">Select...</option>
                          {referees.map((ref) => (
                            <option key={ref.id} value={ref.id}>
                              {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* ASSISTANT 5 */}
                {g.needsAssistant5 && (
                  <div className="mb-1 bg-gray-50 p-2 rounded">
                    {g.assistant5Id ? (
                      <div className="flex items-center justify-between">
                        <p className="text-xs">
                          A5: {g.assistant5Name}{g.assistant5League ? ` - ${g.assistant5League}` : ""}
                        </p>
                        <button
                          onClick={() => unassignReferee(g.id, "assistant5")}
                          className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <label className="text-xs whitespace-nowrap">A5:</label>
                        <select
                          className="p-1 border rounded flex-1 text-xs"
                          defaultValue=""
                          onChange={(e) => {
                            if (e.target.value) {
                              assignReferee(g.id, e.target.value, "assistant5");
                              e.target.value = "";
                            }
                          }}
                        >
                          <option value="">Select...</option>
                          {referees.map((ref) => (
                            <option key={ref.id} value={ref.id}>
                              {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* OTHER OFFICIALS */}
                {(g.needsTimeKeeper || g.needsPlayerMovementRecorder || g.needsStatistician || g.needsTechnicalZoneManager) && (
                  <div className="mt-1 bg-blue-50 p-2 rounded space-y-1">
                    <h5 className="text-xs font-semibold text-gray-700">Other Officials:</h5>
                    
                    {/* TIME KEEPER */}
                    {g.needsTimeKeeper && (
                      <div className="flex items-center gap-1">
                        {g.timeKeeperId ? (
                          <><span className="text-xs">TK: {g.timeKeeperName}</span>
                          <button onClick={() => unassignReferee(g.id, "timekeeper")} className="ml-auto px-1 py-0.5 bg-red-500 text-white rounded text-xs">×</button></>
                        ) : (
                          <><label className="text-xs">TK:</label>
                          <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "timekeeper"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select></>
                        )}
                      </div>
                    )}
                    
                    {/* PLAYER MOVEMENT */}
                    {g.needsPlayerMovementRecorder && (
                      <div className="flex items-center gap-1">
                        {g.playerMovementRecorderId ? (
                          <><span className="text-xs">PMR: {g.playerMovementRecorderName}</span>
                          <button onClick={() => unassignReferee(g.id, "playermovement")} className="ml-auto px-1 py-0.5 bg-red-500 text-white rounded text-xs">×</button></>
                        ) : (
                          <><label className="text-xs">PMR:</label>
                          <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "playermovement"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select></>
                        )}
                      </div>
                    )}
                    
                    {/* STATISTICIAN */}
                    {g.needsStatistician && (
                      <div className="flex items-center gap-1">
                        {g.statisticianId ? (
                          <><span className="text-xs">Stats: {g.statisticianName}</span>
                          <button onClick={() => unassignReferee(g.id, "statistician")} className="ml-auto px-1 py-0.5 bg-red-500 text-white rounded text-xs">×</button></>
                        ) : (
                          <><label className="text-xs">Stats:</label>
                          <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "statistician"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select></>
                        )}
                      </div>
                    )}
                    
                    {/* TECHNICAL ZONE */}
                    {g.needsTechnicalZoneManager && (
                      <div className="flex items-center gap-1">
                        {g.technicalZoneManagerId ? (
                          <><span className="text-xs">TZM: {g.technicalZoneManagerName}</span>
                          <button onClick={() => unassignReferee(g.id, "technicalzone")} className="ml-auto px-1 py-0.5 bg-red-500 text-white rounded text-xs">×</button></>
                        ) : (
                          <><label className="text-xs">TZM:</label>
                          <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "technicalzone"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select></>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>
                {/* RIGHT SIDE - STATUS & BILLING */}
                <div className="col-span-2 space-y-3">
                  {/* STATUS */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`px-4 py-2 rounded text-white font-semibold ${
                        g.status === "pending"
                          ? "bg-yellow-600"
                          : g.status === "assigned"
                          ? "bg-blue-600"
                          : g.status === "scheduled"
                          ? "bg-green-700"
                          : g.status === "referee-declined"
                          ? "bg-orange-600"
                          : g.status === "completed"
                          ? "bg-gray-700"
                          : "bg-gray-500"
                      }`}
                    >
                      {g.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* BILLING - Only show if completed */}
                  {g.status === "completed" && (
                    <div className="px-2 space-y-2">
                      <div className="flex items-center justify-between mb-2 border-b pb-1">
                        <h4 className="text-xs font-bold">
                          Billing Rates
                          {g.competition && (
                            <span className="block text-[10px] font-normal text-gray-600 mt-0.5">
                              ({g.competition})
                            </span>
                          )}
                        </h4>
                        <button
                          onClick={() => applyAllDefaultRates(g)}
                          className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] hover:bg-green-700"
                        >
                          Apply All Defaults
                        </button>
                      </div>
                      
                      {/* Main Referee Billing */}
                      {g.assignedRefereeId && (
                        <div>
                          <label className="block text-xs font-semibold mb-1">
                            Referee: {g.assignedRefereeName}
                            {getDefaultRate(g, "referee") && !g.billing_referee && (
                              <span className={`ml-2 font-normal text-[10px] ${
                                isCompetitionRate(g, "referee") ? "text-purple-600" : "text-blue-600"
                              }`}>
                                ({isCompetitionRate(g, "referee") ? g.competition : "Default"}: R{getDefaultRate(g, "referee")})
                              </span>
                            )}
                          </label>
                          {editingBilling[`${g.id}_referee`] ? (
                            <div className="flex gap-1">
                              <div className="relative flex-1">
                                <span className="absolute left-2 top-1.5 text-gray-600 text-xs">R</span>
                                <input
                                  type="number"
                                  className="w-full p-1.5 pl-6 border rounded text-xs"
                                  placeholder="0"
                                  defaultValue={g.billing_referee || getDefaultRate(g, "referee") || ""}
                                  onBlur={(e) => {
                                    if (e.target.value) {
                                      updateGameBilling(g.id, "referee", e.target.value);
                                    }
                                    toggleEditBilling(g.id, "referee");
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.target.blur();
                                    }
                                  }}
                                  autoFocus
                                />
                              </div>
                              <button
                                onClick={() => toggleEditBilling(g.id, "referee")}
                                className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {(g.billing_referee || getDefaultRate(g, "referee")) ? (
                                <>
                                  <p className="text-sm font-semibold text-green-600">
                                    R{g.billing_referee || getDefaultRate(g, "referee")}
                                  </p>
                                  <button
                                    onClick={() => {
                                      if (!g.billing_referee && getDefaultRate(g, "referee")) {
                                        applyDefaultRate(g.id, "referee", getDefaultRate(g, "referee"));
                                      }
                                      toggleEditBilling(g.id, "referee");
                                    }}
                                    className="ml-auto px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                                  >
                                    Edit
                                  </button>
                                </>
                              ) : (
                                <p className="text-xs text-gray-500">No rate set</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* AR1 Billing */}
                      {g.assistantReferee1Id && renderBillingRow(g, "assistant1", "AR1", g.assistantReferee1Name)}

                      {/* AR2 Billing */}
                      {g.assistantReferee2Id && renderBillingRow(g, "assistant2", "AR2", g.assistantReferee2Name)}

                      {/* Assistant 4 Billing */}
                      {g.assistant4Id && renderBillingRow(g, "assistant4", "A4", g.assistant4Name)}

                      {/* Assistant 5 Billing */}
                      {g.assistant5Id && renderBillingRow(g, "assistant5", "A5", g.assistant5Name)}

                      {/* Timekeeper Billing */}
                      {g.timeKeeperId && renderBillingRow(g, "timekeeper", "Timekeeper", g.timeKeeperName)}

                      {/* Player Movement Recorder Billing */}
                      {g.playerMovementRecorderId && renderBillingRow(g, "playermovement", "PMR", g.playerMovementRecorderName)}

                      {/* Statistician Billing */}
                      {g.statisticianId && renderBillingRow(g, "statistician", "Stats", g.statisticianName)}

                      {/* Technical Zone Manager Billing */}
                      {g.technicalZoneManagerId && renderBillingRow(g, "technicalzone", "TZM", g.technicalZoneManagerName)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
            </div>
          )
          )}
        </div>

        {/* SECTION 2: ASSIGNED GAMES */}
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between cursor-pointer hover:bg-blue-700 transition-colors"
            onClick={() => toggleSection('assigned')}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{collapsedSections.assigned ? '▶' : '▼'}</span>
              <h3 className="text-xl font-bold">📋 Assigned Games</h3>
            </div>
            <span className="bg-white text-blue-600 px-3 py-1 rounded-full font-bold">
              {assignedGames.length}
            </span>
          </div>
          {!collapsedSections.assigned && (assignedGames.length === 0 ? (
            <p className="text-gray-500 p-6">No assigned games.</p>
          ) : (
            <div className="p-4 space-y-4">
              {assignedGames.map((g) => (
            <div
              key={g.id}
              className={`p-4 rounded shadow border-l-4 ${
                g.refereeStatus === "accepted"
                  ? "bg-white border-green-600"
                  : g.refereeStatus === "declined"
                  ? "bg-red-100 border-red-600"
                  : "bg-white border-blue-600"
              }`}
            >
              {/* GAME INFO - HORIZONTAL LAYOUT */}
              <div className="grid grid-cols-12 gap-4 items-start mb-4">
                {/* LEFT SIDE - GAME DETAILS */}
                <div className="col-span-5 space-y-2">
                  {/* Date & Time */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Date & Time:</p>
                    <p className="text-gray-900">{g.date} @ {g.time}</p>
                  </div>
                  
                  {/* Venue & Field */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Venue & Field:</p>
                    <p className="text-gray-900">{g.venue} (Field {g.field})</p>
                  </div>
                  
                  {/* Teams */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Teams:</p>
                    <p className="text-gray-900 font-semibold">{g.teamA} vs {g.teamB}</p>
                  </div>
                  
                  {/* Age & Competition */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Age & Competition:</p>
                    <p className="text-gray-900">{g.ageGroup} • {g.competition}</p>
                  </div>
                  
                  {/* Coach */}
                  <div>
                    <p className="text-xs text-gray-500">Requested by: {g.coachName}</p>
                  </div>
                </div>

                {/* MIDDLE - REFEREE ASSIGNMENTS */}
                <div className="col-span-5 border-l border-r px-4">
                  {/* MAIN REFEREE */}
                  <div className="mb-2">
                    {g.assignedRefereeId ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Main Referee:</p>
                          <p className="font-semibold text-sm">{g.assignedRefereeName}</p>
                          {g.assignedRefereeLeague && (
                            <p className="text-xs text-gray-500">{g.assignedRefereeLeague}</p>
                          )}
                        </div>
                        <button
                          onClick={() => unassignReferee(g.id, "referee")}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      g.needsReferee && (
                        <>
                          <p className="text-xs text-gray-600 mb-1">Main Referee:</p>
                          <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "referee"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select>
                        </>
                      )
                    )}
                  </div>

                  {/* AR1 */}
                  {(g.assistantReferee1Id || g.needsAssistantReferee) && (
                    <div className="mb-2">
                      {g.assistantReferee1Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">AR1:</p>
                            <p className="font-semibold text-xs">{g.assistantReferee1Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant1")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">AR1:</p>
                        <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant1"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* AR2 */}
                  {(g.assistantReferee2Id || g.needsAssistantReferee) && (
                    <div className="mb-2">
                      {g.assistantReferee2Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">AR2:</p>
                            <p className="font-semibold text-xs">{g.assistantReferee2Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant2")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">AR2:</p>
                        <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant2"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* A4 */}
                  {(g.assistant4Id || g.needsAssistant4) && (
                    <div className="mb-2">
                      {g.assistant4Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">A4:</p>
                            <p className="font-semibold text-xs">{g.assistant4Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant4")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">A4:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant4"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* A5 */}
                  {(g.assistant5Id || g.needsAssistant5) && (
                    <div className="mb-2">
                      {g.assistant5Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">A5:</p>
                            <p className="font-semibold text-xs">{g.assistant5Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant5")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">A5:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant5"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* Timekeeper */}
                  {(g.timeKeeperId || g.needsTimeKeeper) && (
                    <div className="mb-2">
                      {g.timeKeeperId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">Timekeeper:</p>
                            <p className="font-semibold text-xs">{g.timeKeeperName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "timekeeper")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">Timekeeper:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "timekeeper"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* PMR */}
                  {(g.playerMovementRecorderId || g.needsPlayerMovementRecorder) && (
                    <div className="mb-2">
                      {g.playerMovementRecorderId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">PMR:</p>
                            <p className="font-semibold text-xs">{g.playerMovementRecorderName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "playermovement")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">PMR:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "playermovement"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* Statistician */}
                  {(g.statisticianId || g.needsStatistician) && (
                    <div className="mb-2">
                      {g.statisticianId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">Stats:</p>
                            <p className="font-semibold text-xs">{g.statisticianName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "statistician")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">Stats:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "statistician"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* TZM */}
                  {(g.technicalZoneManagerId || g.needsTechnicalZoneManager) && (
                    <div className="mb-2">
                      {g.technicalZoneManagerId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">TZM:</p>
                            <p className="font-semibold text-xs">{g.technicalZoneManagerName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "technicalzone")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">TZM:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "technicalzone"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}
                </div>
                {/* RIGHT SIDE - STATUS & BILLING */}
                <div className="col-span-2 space-y-3">
                  {/* STATUS */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`px-4 py-2 rounded text-white font-semibold ${
                        g.status === "pending"
                          ? "bg-yellow-600"
                          : g.status === "assigned"
                          ? "bg-blue-600"
                          : g.status === "scheduled"
                          ? "bg-green-700"
                          : g.status === "referee-declined"
                          ? "bg-orange-600"
                          : g.status === "completed"
                          ? "bg-gray-700"
                          : "bg-gray-500"
                      }`}
                    >
                      {g.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* BILLING - Only show if completed */}
                  {g.status === "completed" && (
                    <div className="px-2 space-y-2">
                      <h4 className="text-xs font-bold text-center mb-2 border-b pb-1">
                        Billing Rates
                        {g.competition && (
                          <span className="block text-[10px] font-normal text-gray-600 mt-0.5">
                            ({g.competition})
                          </span>
                        )}
                      </h4>
                      
                      {/* Main Referee Billing */}
                      {g.assignedRefereeId && renderBillingRow(g, "referee", "Referee", g.assignedRefereeName)}

                      {/* AR1 Billing */}
                      {g.assistantReferee1Id && renderBillingRow(g, "assistant1", "AR1", g.assistantReferee1Name)}

                      {/* AR2 Billing */}
                      {g.assistantReferee2Id && renderBillingRow(g, "assistant2", "AR2", g.assistantReferee2Name)}

                      {/* Assistant 4 Billing */}
                      {g.assistant4Id && renderBillingRow(g, "assistant4", "A4", g.assistant4Name)}

                      {/* Assistant 5 Billing */}
                      {g.assistant5Id && renderBillingRow(g, "assistant5", "A5", g.assistant5Name)}

                      {/* Timekeeper Billing */}
                      {g.timeKeeperId && renderBillingRow(g, "timekeeper", "Timekeeper", g.timeKeeperName)}

                      {/* Player Movement Recorder Billing */}
                      {g.playerMovementRecorderId && renderBillingRow(g, "playermovement", "PMR", g.playerMovementRecorderName)}

                      {/* Statistician Billing */}
                      {g.statisticianId && renderBillingRow(g, "statistician", "Stats", g.statisticianName)}

                      {/* Technical Zone Manager Billing */}
                      {g.technicalZoneManagerId && renderBillingRow(g, "technicalzone", "TZM", g.technicalZoneManagerName)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
            </div>
          )
          )}
        </div>

        {/* SECTION 3: SCHEDULED GAMES */}
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-green-600 text-white p-4 rounded-t-lg flex items-center justify-between cursor-pointer hover:bg-green-700 transition-colors"
            onClick={() => toggleSection('scheduled')}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{collapsedSections.scheduled ? '▶' : '▼'}</span>
              <h3 className="text-xl font-bold">✓ Scheduled Games</h3>
            </div>
            <span className="bg-white text-green-600 px-3 py-1 rounded-full font-bold">
              {scheduledGames.length}
            </span>
          </div>
          {!collapsedSections.scheduled && (scheduledGames.length === 0 ? (
            <p className="text-gray-500 p-6">No scheduled games.</p>
          ) : (
            <div className="p-4 space-y-4">
              {scheduledGames.map((g) => (
            <div
              key={g.id}
              className={`p-4 rounded shadow border-l-4 ${
                g.refereeStatus === "accepted"
                  ? "bg-white border-green-600"
                  : g.refereeStatus === "declined"
                  ? "bg-red-100 border-red-600"
                  : "bg-white border-blue-600"
              }`}
            >
              {/* GAME INFO - HORIZONTAL LAYOUT */}
              <div className="grid grid-cols-12 gap-4 items-start mb-4">
                {/* LEFT SIDE - GAME DETAILS */}
                <div className="col-span-5 space-y-2">
                  {/* Date & Time */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Date & Time:</p>
                    <p className="text-gray-900">{g.date} @ {g.time}</p>
                  </div>
                  
                  {/* Venue & Field */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Venue & Field:</p>
                    <p className="text-gray-900">{g.venue} (Field {g.field})</p>
                  </div>
                  
                  {/* Teams */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Teams:</p>
                    <p className="text-gray-900 font-semibold">{g.teamA} vs {g.teamB}</p>
                  </div>
                  
                  {/* Age & Competition */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Age & Competition:</p>
                    <p className="text-gray-900">{g.ageGroup} • {g.competition}</p>
                  </div>
                  
                  {/* Coach */}
                  <div>
                    <p className="text-xs text-gray-500">Requested by: {g.coachName}</p>
                  </div>
                </div>

                {/* MIDDLE - REFEREE ASSIGNMENTS */}
                <div className="col-span-5 border-l border-r px-4">
                  {/* MAIN REFEREE */}
                  <div className="mb-2">
                    {g.assignedRefereeId ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600">Main Referee:</p>
                          <p className="font-semibold text-sm">{g.assignedRefereeName}</p>
                          {g.assignedRefereeLeague && (
                            <p className="text-xs text-gray-500">{g.assignedRefereeLeague}</p>
                          )}
                        </div>
                        <button
                          onClick={() => unassignReferee(g.id, "referee")}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      g.needsReferee && (
                        <>
                          <p className="text-xs text-gray-600 mb-1">Main Referee:</p>
                          <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "referee"); e.target.value = ""; } }}>
                            <option value="">Select...</option>
                            {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                          </select>
                        </>
                      )
                    )}
                  </div>

                  {/* AR1 */}
                  {(g.assistantReferee1Id || g.needsAssistantReferee) && (
                    <div className="mb-2">
                      {g.assistantReferee1Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">AR1:</p>
                            <p className="font-semibold text-xs">{g.assistantReferee1Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant1")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">AR1:</p>
                        <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant1"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* AR2 */}
                  {(g.assistantReferee2Id || g.needsAssistantReferee) && (
                    <div className="mb-2">
                      {g.assistantReferee2Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">AR2:</p>
                            <p className="font-semibold text-xs">{g.assistantReferee2Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant2")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">AR2:</p>
                        <select className="w-full p-0.5 border rounded text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant2"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* A4 */}
                  {(g.assistant4Id || g.needsAssistant4) && (
                    <div className="mb-2">
                      {g.assistant4Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">A4:</p>
                            <p className="font-semibold text-xs">{g.assistant4Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant4")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">A4:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant4"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* A5 */}
                  {(g.assistant5Id || g.needsAssistant5) && (
                    <div className="mb-2">
                      {g.assistant5Id ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">A5:</p>
                            <p className="font-semibold text-xs">{g.assistant5Name}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "assistant5")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">A5:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "assistant5"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* Timekeeper */}
                  {(g.timeKeeperId || g.needsTimeKeeper) && (
                    <div className="mb-2">
                      {g.timeKeeperId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">Timekeeper:</p>
                            <p className="font-semibold text-xs">{g.timeKeeperName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "timekeeper")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">Timekeeper:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "timekeeper"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* PMR */}
                  {(g.playerMovementRecorderId || g.needsPlayerMovementRecorder) && (
                    <div className="mb-2">
                      {g.playerMovementRecorderId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">PMR:</p>
                            <p className="font-semibold text-xs">{g.playerMovementRecorderName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "playermovement")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">PMR:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "playermovement"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* Statistician */}
                  {(g.statisticianId || g.needsStatistician) && (
                    <div className="mb-2">
                      {g.statisticianId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">Stats:</p>
                            <p className="font-semibold text-xs">{g.statisticianName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "statistician")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">Stats:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "statistician"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}

                  {/* TZM */}
                  {(g.technicalZoneManagerId || g.needsTechnicalZoneManager) && (
                    <div className="mb-2">
                      {g.technicalZoneManagerId ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-600">TZM:</p>
                            <p className="font-semibold text-xs">{g.technicalZoneManagerName}</p>
                          </div>
                          <button onClick={() => unassignReferee(g.id, "technicalzone")} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                        </div>
                      ) : (
                        <><p className="text-xs text-gray-600 mb-1">TZM:</p>
                        <select className="p-0.5 border rounded flex-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { assignReferee(g.id, e.target.value, "technicalzone"); e.target.value = ""; } }}>
                          <option value="">Select...</option>
                          {referees.map((ref) => (<option key={ref.id} value={ref.id}>{ref.fullName}</option>))}
                        </select></>
                      )}
                    </div>
                  )}
                </div>
                {/* RIGHT SIDE - STATUS & BILLING */}
                <div className="col-span-2 space-y-3">
                  {/* STATUS */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`px-4 py-2 rounded text-white font-semibold ${
                        g.status === "pending"
                          ? "bg-yellow-600"
                          : g.status === "assigned"
                          ? "bg-blue-600"
                          : g.status === "scheduled"
                          ? "bg-green-700"
                          : g.status === "referee-declined"
                          ? "bg-orange-600"
                          : g.status === "completed"
                          ? "bg-gray-700"
                          : "bg-gray-500"
                      }`}
                    >
                      {g.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* BILLING - Only show if completed */}
                  {g.status === "completed" && (
                    <div className="px-2 space-y-2">
                      <h4 className="text-xs font-bold text-center mb-2 border-b pb-1">
                        Billing Rates
                        {g.competition && (
                          <span className="block text-[10px] font-normal text-gray-600 mt-0.5">
                            ({g.competition})
                          </span>
                        )}
                      </h4>
                      
                      {/* Main Referee Billing */}
                      {g.assignedRefereeId && renderBillingRow(g, "referee", "Referee", g.assignedRefereeName)}

                      {/* AR1 Billing */}
                      {g.assistantReferee1Id && renderBillingRow(g, "assistant1", "AR1", g.assistantReferee1Name)}

                      {/* AR2 Billing */}
                      {g.assistantReferee2Id && renderBillingRow(g, "assistant2", "AR2", g.assistantReferee2Name)}

                      {/* Assistant 4 Billing */}
                      {g.assistant4Id && renderBillingRow(g, "assistant4", "A4", g.assistant4Name)}

                      {/* Assistant 5 Billing */}
                      {g.assistant5Id && renderBillingRow(g, "assistant5", "A5", g.assistant5Name)}

                      {/* Timekeeper Billing */}
                      {g.timeKeeperId && renderBillingRow(g, "timekeeper", "Timekeeper", g.timeKeeperName)}

                      {/* Player Movement Recorder Billing */}
                      {g.playerMovementRecorderId && renderBillingRow(g, "playermovement", "PMR", g.playerMovementRecorderName)}

                      {/* Statistician Billing */}
                      {g.statisticianId && renderBillingRow(g, "statistician", "Stats", g.statisticianName)}

                      {/* Technical Zone Manager Billing */}
                      {g.technicalZoneManagerId && renderBillingRow(g, "technicalzone", "TZM", g.technicalZoneManagerName)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
            </div>
          )
          )}
        </div>

        {/* SECTION 4: COMPLETED GAMES */}
        <div className="bg-white rounded-lg shadow">
          <div 
            className="bg-gray-700 text-white p-4 rounded-t-lg flex items-center justify-between cursor-pointer hover:bg-gray-800 transition-colors"
            onClick={() => toggleSection('completed')}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{collapsedSections.completed ? '▶' : '▼'}</span>
              <h3 className="text-xl font-bold">📁 Completed Games</h3>
            </div>
            <span className="bg-white text-gray-700 px-3 py-1 rounded-full font-bold">
              {completedGames.length}
            </span>
          </div>
          {!collapsedSections.completed && (completedGames.length === 0 ? (
            <p className="text-gray-500 p-6">No completed games.</p>
          ) : (
            <div className="p-4 space-y-4">
              {completedGames.map((g) => (
            <div
              key={g.id}
              className={`p-4 rounded shadow border-l-4 ${
                g.status === 'cancelled' ? 'border-red-500' : 'border-gray-500'
              } bg-white`}
            >
              {/* GAME INFO - HORIZONTAL LAYOUT */}
              <div className="grid grid-cols-12 gap-4 items-start mb-4">
                {/* LEFT SIDE - GAME DETAILS */}
                <div className="col-span-5 space-y-2">
                  {/* Date & Time */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Date & Time:</p>
                    <p className="text-gray-900">{g.date} @ {g.time}</p>
                  </div>
                  
                  {/* Venue & Field */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Venue & Field:</p>
                    <p className="text-gray-900">{g.venue} (Field {g.field})</p>
                  </div>
                  
                  {/* Teams */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Teams:</p>
                    <p className="text-gray-900 font-semibold">{g.teamA} vs {g.teamB}</p>
                  </div>
                  
                  {/* Age & Competition */}
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Age & Competition:</p>
                    <p className="text-gray-900">{g.ageGroup} • {g.competition}</p>
                  </div>
                  
                  {/* Coach */}
                  <div>
                    <p className="text-xs text-gray-500">Requested by: {g.coachName}</p>
                  </div>
                </div>

                {/* MIDDLE - REFEREE ASSIGNMENTS */}
                <div className="col-span-5 border-l border-r px-4">
                  {/* MAIN REFEREE */}
                  {g.assignedRefereeId && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Main Referee:</p>
                      <p className="font-semibold text-sm">{g.assignedRefereeName}</p>
                    </div>
                  )}

                  {/* AR1 */}
                  {g.assistantReferee1Id && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">AR1:</p>
                      <p className="font-semibold text-xs">{g.assistantReferee1Name}</p>
                    </div>
                  )}

                  {/* AR2 */}
                  {g.assistantReferee2Id && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">AR2:</p>
                      <p className="font-semibold text-xs">{g.assistantReferee2Name}</p>
                    </div>
                  )}

                  {/* Other officials */}
                  {g.assistant4Id && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">A4:</span> {g.assistant4Name}</p></div>}
                  {g.assistant5Id && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">A5:</span> {g.assistant5Name}</p></div>}
                  {g.timeKeeperId && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">TK:</span> {g.timeKeeperName}</p></div>}
                  {g.playerMovementRecorderId && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">PMR:</span> {g.playerMovementRecorderName}</p></div>}
                  {g.statisticianId && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">Stats:</span> {g.statisticianName}</p></div>}
                  {g.technicalZoneManagerId && <div className="mb-1"><p className="text-xs"><span className="text-gray-600">TZM:</span> {g.technicalZoneManagerName}</p></div>}
                </div>

                {/* RIGHT SIDE - STATUS & BILLING */}
                <div className="col-span-2 space-y-3">
                  {/* STATUS */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`px-3 py-1 rounded text-white font-semibold text-xs ${
                        g.status === 'cancelled' ? "bg-red-600" : "bg-gray-700"
                      }`}
                    >
                      {g.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* BILLING */}
                  {g.status === "completed" && (
                    <div className="px-2 space-y-2">
                      <h4 className="text-xs font-bold text-center mb-2 border-b pb-1">
                        Billing Rates
                        {g.competition && (
                          <span className="block text-[10px] font-normal text-gray-600 mt-0.5">
                            ({g.competition})
                          </span>
                        )}
                      </h4>
                      
                      {/* Main Referee Billing */}
                      {g.assignedRefereeId && renderBillingRow(g, "referee", "Referee", g.assignedRefereeName)}

                      {/* AR1 Billing */}
                      {g.assistantReferee1Id && renderBillingRow(g, "assistant1", "AR1", g.assistantReferee1Name)}

                      {/* AR2 Billing */}
                      {g.assistantReferee2Id && renderBillingRow(g, "assistant2", "AR2", g.assistantReferee2Name)}

                      {/* Assistant 4 Billing */}
                      {g.assistant4Id && renderBillingRow(g, "assistant4", "A4", g.assistant4Name)}

                      {/* Assistant 5 Billing */}
                      {g.assistant5Id && renderBillingRow(g, "assistant5", "A5", g.assistant5Name)}

                      {/* Timekeeper Billing */}
                      {g.timeKeeperId && renderBillingRow(g, "timekeeper", "Timekeeper", g.timeKeeperName)}

                      {/* Player Movement Recorder Billing */}
                      {g.playerMovementRecorderId && renderBillingRow(g, "playermovement", "PMR", g.playerMovementRecorderName)}

                      {/* Statistician Billing */}
                      {g.statisticianId && renderBillingRow(g, "statistician", "Stats", g.statisticianName)}

                      {/* Technical Zone Manager Billing */}
                      {g.technicalZoneManagerId && renderBillingRow(g, "technicalzone", "TZM", g.technicalZoneManagerName)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
            </div>
          )
          )}
        </div>
      </div>

      {/* REFEREE LIST */}
      <div className="mt-8 bg-white p-4 rounded shadow">
        <h3 className="text-xl font-bold mb-3">
          Available Referees ({referees.length})
        </h3>
        {referees.length === 0 ? (
          <p className="text-gray-500">No approved referees yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {referees.map((ref) => (
              <div key={ref.id} className="p-3 border rounded">
                <p className="font-semibold">
                  {ref.fullName}{ref.currentLeague ? ` - ${ref.currentLeague}` : ""}
                </p>
                <p className="text-sm text-gray-600">{ref.email}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DECLINED GAME POPUP */}
      {declinedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDeclinedGame(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-4xl text-red-600">✕</span>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2 text-red-600">Game Declined</h2>
            
            <div className="space-y-2 mb-6">
              <p className="text-center text-gray-700">
                <span className="font-semibold">{declinedGame.assignedRefereeName}</span> has declined the game:
              </p>
              <div className="bg-gray-50 p-3 rounded text-center">
                <p className="font-semibold">{declinedGame.teamA} vs {declinedGame.teamB}</p>
                <p className="text-sm text-gray-600">{declinedGame.date} @ {declinedGame.time}</p>
                <p className="text-sm text-gray-600">{declinedGame.venue} (Field {declinedGame.field})</p>
              </div>
              <p className="text-center text-gray-600 text-sm mt-3">
                Please assign a different referee.
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setDeclinedGame(null)}
                className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
